import React from 'react';
import { Link } from 'react-router-dom';
import TopNav from './TopNav';
import SolutionsFooter from './SolutionsFooter';
import HowToEngage from './HowToEngage';
import useSEO from '../hooks/useSEO';

function PublicPolicyExample({ logo }) {
  useSEO({
    title: 'Public Policy Simulation',
    description: 'Simulate regulatory and policy changes to understand second-order effects before implementation creates surprises.'
  });

  return (
    <div className="min-h-screen bg-white">
      <TopNav />

      <main className="max-w-5xl mx-auto px-8 py-16 md:py-24">
        <div className="text-center mb-16">
          <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Policy Impact Modeling</p>
          <h1 className="text-4xl md:text-5xl font-medium text-gray-900 mb-6 leading-tight tracking-tight">Test policy ideas before they become unintended consequences</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">Simulate regulatory and policy changes to understand second-order effects—before implementation creates surprises.</p>
        </div>

        <div className="mb-16">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6 text-center">Who this is for</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center"><p className="font-medium text-gray-900 mb-2">Policy Analysts</p><p className="text-sm text-gray-600">Quantifying impacts before proposals advance</p></div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center"><p className="font-medium text-gray-900 mb-2">Agency Leaders</p><p className="text-sm text-gray-600">Making evidence-based regulatory decisions</p></div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center"><p className="font-medium text-gray-900 mb-2">Think Tanks & Researchers</p><p className="text-sm text-gray-600">Building credible policy recommendations</p></div>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6 text-center">Decisions this improves</h2>
          <div className="bg-white border border-gray-200 rounded-lg p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">How policy changes affect different stakeholder groups</p></div>
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">What unintended behavioral responses might emerge</p></div>
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">How to phase implementation to minimize disruption</p></div>
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">What the fiscal impact will be under different scenarios</p></div>
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">Trade-offs between policy objectives</p></div>
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">How to design more effective incentive structures</p></div>
            </div>
          </div>
        </div>

        <div className="mb-16">
          <div className="bg-gray-900 text-white rounded-lg p-8 md:p-10">
            <p className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-4">Example: State Economic Development</p>
            <p className="text-lg leading-relaxed mb-6">A state agency simulated proposed tax incentives across different sectors, modeling behavioral responses, migration patterns, and revenue impacts. The analysis revealed that a targeted approach focusing on three key industries would generate 60% of the job creation at 40% of the fiscal cost—avoiding market distortions that broader incentives would have created.</p>
            <p className="text-gray-400 text-sm">The impact: Policy redesign projected to create 12,000 jobs with $180M less fiscal impact.</p>
          </div>
        </div>

                <HowToEngage
          platformDescription="Model policy scenarios, test assumptions, and share analyses with stakeholders. Ideal for research teams who want ongoing analytical capability."
          consultingDescription="Partner with our policy consultants to build rigorous impact models, run stakeholder workshops, and deliver defensible recommendations."
        />

        <div className="pt-8 border-t border-gray-200">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6">Related solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/solutions/financial-services" className="text-gray-700 hover:text-gray-900 text-sm">Risk + Portfolio Analysis →</Link>
            <Link to="/solutions/energy-utilities" className="text-gray-700 hover:text-gray-900 text-sm">Grid + Demand Management →</Link>
            <Link to="/solutions/workforce" className="text-gray-700 hover:text-gray-900 text-sm">Capacity + Backlog Planning →</Link>
          </div>
        </div>
      </main>

      <SolutionsFooter />
    </div>
  );
}

export default PublicPolicyExample;
